import { useTrackClickedActionItem } from '@air/marketing-analytics';
import { CSS, Text, TextProps } from '@air/marketing-primitives';
import { noop } from 'lodash';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { MouseEventHandler } from 'react';

import { Routes } from '~/old-marketing/constants/Routes';
import { LINK } from '~/old-marketing/constants/testIds';

export type LinkProps = Pick<NextLinkProps, 'href' | 'passHref'> &
  Pick<TextProps, 'children' | 'css' | 'size' | 'transform'> & {
    href: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    type?: 'external' | 'internal';
  };

const sharedLinkCSS: CSS = {
  display: 'inline-flex',
  alignItems: 'center',
  color: '$macaw350',
  fontWeight: '$medium',
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
  },
};

export const Link = ({
  children,
  css,
  href = Routes.root,
  onClick = noop,
  passHref = true,
  size,
  transform,
  type = 'internal',
}: LinkProps) => {
  const { trackClickedActionItem } = useTrackClickedActionItem();

  const isExternal = type === 'external';

  const onLinkClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    trackClickedActionItem({
      location: window.location.href,
      destination: href,
      destinationType: type,
    });

    onClick(event);
  };

  if (isExternal) {
    return (
      <Text
        as="a"
        css={{ ...sharedLinkCSS, ...css }}
        data-testid={LINK}
        href={href}
        onClick={onLinkClick}
        size={size}
        target="_blank"
        transform={transform}
      >
        {children}
      </Text>
    );
  }

  return (
    <NextLink href={href} passHref={passHref}>
      <Text
        as="a"
        css={{ ...sharedLinkCSS, ...css }}
        data-testid={LINK}
        onClick={onLinkClick}
        size={size}
        transform={transform}
      >
        {children}
      </Text>
    </NextLink>
  );
};
